import { useContext, useEffect, useState } from 'react';
import {
  AccordionToggleEventHandler,
  Accordion,
  AccordionToggleEvent,
} from '@fluentui/react-components';
import {
  Skill,
  EmployeeSkillsByCategoryDTO,
  OptionSet,
  EmployeeSkill,
} from 'src/tabs/models/SkillsInterfaces';
import {
  AddSkillButtonCategory,
  AddSkillButtonEmptyCategory,
  AddSkillButtonEmptyCategoryMobile,
  CategoryColumnTitle,
  EditButton,
  EmptySkillsCategoryWrapper,
  FindExpertButton,
  MySkillsCategoryAccordion,
  MySkillsContainer,
  MySkillsHeader,
  MySkillsList,
  MySkillsListContainer,
  MySkillsTitle,
  SkillBackground,
  SkillItem,
  SkillItemTitle,
  SkillsContainer,
  SkillsItemHeader,
  StyledAccordionHeader,
  StyledAccordionPanel,
  MySkillsAccordionItem,
} from './styles/SkillsMainContainer.styled';
import { Edit12Regular, Search20Filled } from '@fluentui/react-icons';
import { PrimaryButton } from 'src/styles/globalStyles/buttonStyles.styled';
import { SkillsLevelIndicator } from '../SkillsLevelIndicator/SkillsLevelIndicator';
import { SkillsStatusIndicator } from '../SkillsStatusIndicator/SkillsStatusIndicator';
import { SkillsAddPopup } from '../SkillsAddPopup/SkillsAddPopup';
import { SkillsEditPopup } from '../SkillsEditPopup/SkillsEditPopup';
import { Add16Regular, Add20Regular } from '@fluentui/react-icons';
import { TeamsFxContext } from 'src/context/TeamsContext';
import { useGetEmployeeSkillById } from 'src/tabs/hooks/useGetSkillById';
import { SkillsWidgets } from '../SkillsWidgetsContainer/SkillsWidgets';
import { SkillsItemMobile } from './mobile/SkillsItemMobile';
import { AddSkillsButtonCategoryMobile } from './mobile/AddSkillsButtonCategoryMobile';
import { appearance } from 'src/hooks/useAlertMessage';

interface SkillsMainContainerProps {
  employeeSkills: EmployeeSkillsByCategoryDTO[];
  existingSkillIds: string[];
  categories: OptionSet[];
  skillsByCategory: Skill[];
  getEmployeeSkills: () => void;
  handleCategoryChange: (category: string) => void;
  getExperts: (skillId: string) => Promise<void>;
  clearExperts: () => void;
  expertEmployees: EmployeeSkill[];
  getSkills: () => void;
  loadingExperts: boolean;
  showAlert: (message: string, appearance: appearance) => void;
}

export const SkillsMainContainer = ({
  showAlert,
  employeeSkills,
  existingSkillIds,
  categories,
  skillsByCategory,
  getEmployeeSkills,
  expertEmployees,
  getExperts,
  clearExperts,
  loadingExperts,
  handleCategoryChange,
}: SkillsMainContainerProps) => {
  const { employeeSkillById, getEmployeeSkillById, isLoadingEmployeeSkill } =
    useGetEmployeeSkillById();
  const { themeString, isMobile } = useContext(TeamsFxContext);
  const emptyCategories = categories
    .filter((category) => {
      return !employeeSkills.some(
        (skill) => skill.category.value === category.value
      );
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  const [openItems, setOpenItems] = useState([]);
  const [addSkillPopup, setAddSkillPopup] = useState(false);
  const [editSkillPopup, setEditSkillPopup] = useState(false);
  const [findExpertPopup, setFindExpertPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSkillToEdit, setSelectedSkillToEdit] =
    useState<EmployeeSkill>();

  const handleToggle: AccordionToggleEventHandler<string> = (_, data) => {
    setOpenItems(data.openItems);
  };

  const skillsLength = employeeSkills.length;
  const skillsLengthArray = ['-1', ...Array(skillsLength).keys()].map((index) =>
    index.toString()
  );

  const handleMySkillsListToggle = (
    event: AccordionToggleEvent<HTMLElement>
  ) => {
    if (openItems.length === skillsLength + 1) {
      handleToggle(event, {
        openItems: [],
        value: '',
      });
    } else if (openItems.length !== skillsLength + 1 && skillsLength > 0) {
      handleToggle(event, {
        openItems: skillsLengthArray,
        value: '',
      });
    }
  };

  const handleAddSkillButton = (
    e: React.MouseEvent<HTMLButtonElement>,
    skillDTOCategoryValue: string
  ) => {
    e.stopPropagation();
    setSelectedCategory(skillDTOCategoryValue);
    setAddSkillPopup(true);
  };

  const handleEditSkillButton = (
    skillId: string,
    skill: EmployeeSkill,
    skillCategoryLabel?: string
  ) => {
    setSelectedCategory(skillCategoryLabel);
    getEmployeeSkillById(skillId);
    setSelectedSkillToEdit(skill);

    setEditSkillPopup(true);
  };

  const handleAddSkill = (category?: OptionSet) => {
    setSelectedCategory(category && category?.value ? category.value : '');
    setAddSkillPopup(true);
  };

  useEffect(() => {
    setOpenItems(skillsLengthArray);
  }, [employeeSkills]);

  if (findExpertPopup) {
    return (
      <SkillsWidgets
        setFindExpertPopup={setFindExpertPopup}
        categories={categories}
        expertEmployees={expertEmployees}
        getExperts={getExperts}
        skillsByCategory={skillsByCategory}
        clearExperts={clearExperts}
        loadingExperts={loadingExperts}
        handleCategoryChange={handleCategoryChange}
      />
    );
  }
  if (isMobile && addSkillPopup) {
    return (
      <SkillsAddPopup
        onClose={() => setAddSkillPopup(false)}
        categories={categories}
        selectedCategoryOutsidePopup={selectedCategory}
        employeeSkills={employeeSkills}
        existingSkillIds={existingSkillIds}
        getSkills={getEmployeeSkills}
        showAlert={showAlert}
      />
    );
  }

  if (isMobile && editSkillPopup && employeeSkillById) {
    return (
      <SkillsEditPopup
        onClose={() => setEditSkillPopup(false)}
        skillToEditDTO={employeeSkillById}
        skillToEditCategory={selectedCategory}
        selectedSkillToEdit={selectedSkillToEdit}
        getSkills={getEmployeeSkills}
        isLoadingEmployeeSkill={isLoadingEmployeeSkill}
      />
    );
  }

  return (
    <>
      {isMobile && (
        <FindExpertButton
          findExpertPopup={findExpertPopup}
          onClick={() => setFindExpertPopup(true)}
        >
          <Search20Filled />
          Expert
        </FindExpertButton>
      )}
      <SkillsContainer>
        {!isMobile && addSkillPopup && (
          <SkillsAddPopup
            onClose={() => setAddSkillPopup(false)}
            categories={categories}
            selectedCategoryOutsidePopup={selectedCategory}
            employeeSkills={employeeSkills}
            existingSkillIds={existingSkillIds}
            getSkills={getEmployeeSkills}
            showAlert={showAlert}
          />
        )}
        {!isMobile && editSkillPopup && employeeSkillById && (
          <SkillsEditPopup
            onClose={() => setEditSkillPopup(false)}
            skillToEditDTO={employeeSkillById}
            skillToEditCategory={selectedCategory}
            selectedSkillToEdit={selectedSkillToEdit}
            getSkills={getEmployeeSkills}
            isLoadingEmployeeSkill={isLoadingEmployeeSkill}
          />
        )}
        <MySkillsContainer>
          <MySkillsTitle>My Skills</MySkillsTitle>
          <PrimaryButton onClick={() => handleAddSkill()} size="medium">
            <Add20Regular />
            {isMobile ? 'Add' : 'Add skill'}
          </PrimaryButton>
        </MySkillsContainer>
        <MySkillsList>
          <Accordion
            collapsible
            defaultOpenItems="-1"
            openItems={openItems}
            onToggle={(event) => handleMySkillsListToggle(event)}
          >
            <MySkillsAccordionItem value="-1">
              <StyledAccordionHeader isDarkTheme={themeString === 'dark'}>
                <CategoryColumnTitle>Categories</CategoryColumnTitle>{' '}
                {!isMobile && (
                  <MySkillsHeader>
                    <CategoryColumnTitle>Level</CategoryColumnTitle>
                    <CategoryColumnTitle>Attitude</CategoryColumnTitle>
                    <CategoryColumnTitle>Status</CategoryColumnTitle>
                    <CategoryColumnTitle>Actions</CategoryColumnTitle>
                  </MySkillsHeader>
                )}
              </StyledAccordionHeader>
            </MySkillsAccordionItem>
          </Accordion>
          <MySkillsListContainer>
            <Accordion
              openItems={openItems}
              onToggle={handleToggle}
              multiple
              collapsible
            >
              {employeeSkills.map((skillDTO, index) => (
                <MySkillsCategoryAccordion
                  value={index.toString()}
                  key={skillDTO.category.value}
                >
                  <StyledAccordionHeader isDarkTheme={themeString === 'dark'}>
                    <MySkillsTitle>{skillDTO.category?.label}</MySkillsTitle>
                    {isMobile ? (
                      <AddSkillsButtonCategoryMobile
                        setAddSkillPopup={setAddSkillPopup}
                        setSelectedCategory={setSelectedCategory}
                        skillDTO={skillDTO}
                      />
                    ) : (
                      <AddSkillButtonCategory
                        onClick={(e) =>
                          handleAddSkillButton(e, skillDTO.category.value)
                        }
                        size="small"
                      >
                        <Add16Regular />
                        Add
                      </AddSkillButtonCategory>
                    )}
                  </StyledAccordionHeader>
                  {skillDTO.employeeSkills.length &&
                    skillDTO.employeeSkills.map((skill) => (
                      <StyledAccordionPanel key={skill.id}>
                        <SkillBackground>
                          {isMobile ? (
                            <SkillsItemMobile
                              getEmployeeSkillById={getEmployeeSkillById}
                              setEditSkillPopup={setEditSkillPopup}
                              setSelectedCategory={setSelectedCategory}
                              setSelectedSkillToEdit={setSelectedSkillToEdit}
                              skill={skill}
                              skillDTO={skillDTO}
                            />
                          ) : (
                            <>
                              <SkillItemTitle>
                                {skill.skill.name}
                              </SkillItemTitle>
                              <SkillsItemHeader>
                                <SkillItem>
                                  <SkillsLevelIndicator
                                    level={
                                      skill.validatedDate
                                        ? skill.level?.label
                                        : skill.requestedLevel?.label || ''
                                    }
                                  />
                                  <p>
                                    {skill.validatedDate
                                      ? skill.attitude?.label
                                      : skill.requestedAttitude?.label || ''}
                                  </p>
                                  <SkillsStatusIndicator
                                    requestedAttitude={
                                      skill.requestedAttitude?.label
                                    }
                                    requestedLevel={skill.requestedLevel?.label}
                                    validatedBy={skill.validatedBy}
                                    validatedDate={skill.validatedDate}
                                    status={
                                      skill.requestedAttitude ||
                                      skill.requestedLevel
                                        ? '2'
                                        : '1'
                                    }
                                  />
                                  <EditButton
                                    onClick={() =>
                                      handleEditSkillButton(
                                        skill.id,
                                        skill,
                                        skillDTO.category?.label
                                      )
                                    }
                                    size="small"
                                    isMobile={isMobile}
                                  >
                                    <Edit12Regular />
                                    <p>Edit</p>
                                  </EditButton>
                                </SkillItem>
                              </SkillsItemHeader>
                            </>
                          )}
                        </SkillBackground>
                      </StyledAccordionPanel>
                    ))}
                </MySkillsCategoryAccordion>
              ))}
              {emptyCategories.map((category) => {
                return (
                  <EmptySkillsCategoryWrapper key={category.value}>
                    <MySkillsCategoryAccordion
                      value={category.value}
                      key={category.label}
                      disabled
                    >
                      <StyledAccordionHeader
                        isDarkTheme={themeString === 'dark'}
                      >
                        <h1>{category.label}</h1>
                      </StyledAccordionHeader>
                    </MySkillsCategoryAccordion>

                    {isMobile ? (
                      <AddSkillButtonEmptyCategoryMobile
                        onClick={() => handleAddSkill(category)}
                      >
                        <Add16Regular />
                      </AddSkillButtonEmptyCategoryMobile>
                    ) : (
                      <AddSkillButtonEmptyCategory
                        onClick={() => handleAddSkill(category)}
                        size="small"
                      >
                        <Add16Regular />
                        Add
                      </AddSkillButtonEmptyCategory>
                    )}
                  </EmptySkillsCategoryWrapper>
                );
              })}
            </Accordion>
          </MySkillsListContainer>
        </MySkillsList>
      </SkillsContainer>
    </>
  );
};
